import React, { useContext, useEffect, useState, useMemo } from 'react';
import { BackgroundContext, backgroundUrl } from '../DataContexts/BackgroundContext';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import { machineDetails, filterAndSortMachines } from './PlaylistAssign';
import DashboardSection from '../components/DashboardSection';
import { formatUploadedAt } from './BackgroundEditor';
import './PlaylistAssign.css';

// "Background subscriptions" section (DJB-228, r2 DJB-234, C3): every
// machine is subscribed to at most one background at a time, modelled on the
// Ads & Screensaver Subscriptions section (DJB-82). Pick a background on the
// left; the middle column shows its name, id, upload date and image plus its
// subscribed machines; the right column lists the available machines
// (including machines currently subscribed to another background, shown
// with a badge naming it) and machine groups. Assigning a background to a
// machine here moves the machine there — out of whichever background it was
// on before — via BackgroundContext's assignBackground(machineId,
// backgroundId); removing it via unassignBackground(machineId) means the
// machine falls back to the player's default background. Both update the
// context's list locally right away. `active`/`onToggle` hook the section
// into the subpage's independent collapsed-section logic (DJB-83).
const BackgroundAssign = ({ active = true, onToggle }) => {
  const {
    backgrounds,
    loading,
    error,
    fetchBackgrounds,
    assignBackground,
    unassignBackground,
  } = useContext(BackgroundContext);
  const { machines, loading: machinesLoading, error: machinesError } = useContext(MachineContext);
  const { machineGroups, loading: machineGroupsLoading, error: machineGroupsError } = useContext(MachineGroupContext);

  const [selectedBackgroundId, setSelectedBackgroundId] = useState(null);
  const [backgroundSearch, setBackgroundSearch] = useState('');
  const [machineSearch, setMachineSearch] = useState('');
  const [machineSortKey, setMachineSortKey] = useState('name');
  const [machineSortAsc, setMachineSortAsc] = useState(true);
  const [availableMachineSearch, setAvailableMachineSearch] = useState('');
  const [availableSortKey, setAvailableSortKey] = useState('name');
  const [availableSortAsc, setAvailableSortAsc] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);

  // The background list is loaded lazily by the sections that need it (the
  // provider does not fetch on app start)
  useEffect(() => {
    fetchBackgrounds();
  }, [fetchBackgrounds]);

  const selectedBackground = useMemo(
    () => backgrounds.find((bg) => bg.id === selectedBackgroundId) ?? null,
    [backgrounds, selectedBackgroundId]
  );

  const filteredBackgrounds = useMemo(() => {
    if (!backgroundSearch.trim()) return backgrounds;
    const q = backgroundSearch.toLowerCase().trim();
    return backgrounds.filter((bg) => bg.name?.toLowerCase().includes(q));
  }, [backgrounds, backgroundSearch]);

  const subscribedMachinesWithDetails = useMemo(() => {
    if (!selectedBackground?.subscribedMachineIds?.length) return [];
    return selectedBackground.subscribedMachineIds.map((machineId) => {
      const machine = machines?.find((m) => m.id === machineId);
      // A subscribed machine can be missing from the machines list; keep the
      // row (with the id as its name) so it can still be unsubscribed
      return machine ? machineDetails(machine) : { id: machineId, name: machineId, place: '-', post: '-', province: '-' };
    });
  }, [selectedBackground, machines]);

  const subscribedIdsSet = useMemo(
    () => new Set(selectedBackground?.subscribedMachineIds ?? []),
    [selectedBackground]
  );

  // Maps every machine currently subscribed to any background (not just the
  // selected one) to that background's name, so a machine offered as
  // available here can be shown with the background it would move from.
  const currentBackgroundById = useMemo(() => {
    const map = {};
    backgrounds.forEach((bg) => {
      (bg.subscribedMachineIds ?? []).forEach((machineId) => {
        map[machineId] = bg.name;
      });
    });
    return map;
  }, [backgrounds]);

  const availableMachinesWithDetails = useMemo(() => {
    if (!selectedBackground || !machines?.length) return [];
    return machines.filter((m) => !subscribedIdsSet.has(m.id)).map(machineDetails);
  }, [machines, selectedBackground, subscribedIdsSet]);

  const filteredAndSortedMachines = useMemo(
    () => filterAndSortMachines(subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc),
    [subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc]
  );

  const filteredAndSortedAvailableMachines = useMemo(
    () => filterAndSortMachines(availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc),
    [availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc]
  );

  const toggleMachineSort = (key) => {
    if (machineSortKey === key) {
      setMachineSortAsc((a) => !a);
    } else {
      setMachineSortKey(key);
      setMachineSortAsc(true);
    }
  };

  const toggleAvailableSort = (key) => {
    if (availableSortKey === key) {
      setAvailableSortAsc((a) => !a);
    } else {
      setAvailableSortKey(key);
      setAvailableSortAsc(true);
    }
  };

  const handleRemoveMachine = async (machineId) => {
    if (!selectedBackgroundId) return;
    try {
      await unassignBackground(machineId);
    } catch (e) {
      // notification already shown in context
    }
  };

  const handleAddMachine = async (machineId) => {
    if (!selectedBackgroundId) return;
    try {
      await assignBackground(machineId, selectedBackgroundId);
    } catch (e) {
      // notification already shown in context
    }
  };

  const handleRemoveAllMachines = async () => {
    if (!selectedBackgroundId) return;
    for (const m of subscribedMachinesWithDetails) {
      try {
        await unassignBackground(m.id);
      } catch (e) {
        // continue; notification already shown
      }
    }
    setConfirmRemoveAll(false);
  };

  const handleAddAllMachines = async () => {
    if (!selectedBackgroundId) return;
    for (const m of filteredAndSortedAvailableMachines) {
      try {
        await assignBackground(m.id, selectedBackgroundId);
      } catch (e) {
        // continue with rest; notification already shown
      }
    }
  };

  const filteredGroups = useMemo(() => {
    if (!machineGroups?.length) return [];
    if (!groupSearch.trim()) return machineGroups;
    const q = groupSearch.toLowerCase().trim();
    return machineGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [machineGroups, groupSearch]);

  // Machine groups (DJB-234): subscribe every machine of the group that is
  // not subscribed yet, or unsubscribe every machine of the group that is.
  // There is no group endpoint; both loop over the per-machine calls like
  // the ads section does.
  const groupSubscribedCount = (group) =>
    (group.machineIds ?? []).filter((id) => subscribedIdsSet.has(id)).length;

  const handleAddGroup = async (group) => {
    if (!selectedBackgroundId || !group.machineIds?.length) return;
    for (const machineId of group.machineIds) {
      if (subscribedIdsSet.has(machineId)) continue; // skip already subscribed
      try {
        await assignBackground(machineId, selectedBackgroundId);
      } catch (e) {
        // continue; notification already shown
      }
    }
  };

  const handleRemoveGroup = async (group) => {
    if (!selectedBackgroundId || !group.machineIds?.length) return;
    for (const machineId of group.machineIds) {
      if (!subscribedIdsSet.has(machineId)) continue; // skip not subscribed
      try {
        await unassignBackground(machineId);
      } catch (e) {
        // continue; notification already shown
      }
    }
  };

  const sortArrow = (isActive, asc) =>
    isActive ? <span className="ps-sort-arrow">{asc ? '▲' : '▼'}</span> : null;

  const sectionShell = (children) => (
    <DashboardSection
      id="section-background-subscriptions"
      title="Background subscriptions"
      info="Abonneer machines en groepen op achtergronden voor het hoofdscherm."
      isOpen={active}
      onToggle={onToggle}
      bodyId="ps-body-background-subscriptions"
    >
      {children}
    </DashboardSection>
  );

  if (loading || machinesLoading || machineGroupsLoading) {
    return sectionShell(<p className="ps-status">Loading data, please wait...</p>);
  }

  if (error || machinesError || machineGroupsError) {
    return sectionShell(
      <p className="ps-status ps-status--error">
        Error loading data: {error || machinesError || machineGroupsError}
      </p>
    );
  }

  return sectionShell(
    <div className="ps-columns">
      {/* First column: search and list of backgrounds. Uploading new
          backgrounds lives in Media Studio > Background Editor (DJB-227). */}
      <div className="ps-column ps-column--backgrounds">
        <div className="ps-column-head">
          <span className="ps-column-label">Backgrounds</span>
          <span className="ps-column-count">{filteredBackgrounds.length}</span>
        </div>
        <div className="ps-filters">
          <input
            type="text"
            className="ps-input"
            placeholder="Search by background name..."
            value={backgroundSearch}
            onChange={(e) => setBackgroundSearch(e.target.value)}
          />
        </div>
        <div className="ps-list-scroll">
          <ul className="ps-playlist-list">
            {backgrounds.length === 0 && <li className="ps-empty">No backgrounds</li>}
            {backgrounds.length > 0 && filteredBackgrounds.length === 0 && (
              <li className="ps-empty">No matching backgrounds</li>
            )}
            {filteredBackgrounds.map((bg) => (
              <li
                key={bg.id}
                className={`ps-playlist-item${selectedBackgroundId === bg.id ? ' selected' : ''}`}
                onClick={() => setSelectedBackgroundId(bg.id)}
                title={formatUploadedAt(bg.uploadedAt)}
              >
                <div className="ps-playlist-row">
                  <span className="ps-playlist-name">{bg.name}</span>
                  <span className="ps-subs-badge">{bg.subscribedMachineIds.length} subs</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Second column: selected background (name, id, image) and its
          subscribed machines */}
      <div className="ps-column ps-column--subscribed">
        {selectedBackground ? (
          <>
            <div className="ps-selected" data-testid="background-detail">
              <div className="ps-selected-name">{selectedBackground.name}</div>
              <div className="ps-selected-id">
                Background id: <span>{selectedBackground.id}</span>
              </div>
              <div className="ps-selected-id">
                Uploaded: <span>{formatUploadedAt(selectedBackground.uploadedAt)}</span>
              </div>
            </div>
            <div className="ps-ad-image-wrap">
              <img
                src={backgroundUrl(selectedBackground.id)}
                alt={selectedBackground.name}
                className="ps-ad-image"
              />
            </div>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Subscribed machines</h3>
              <span className="ps-column-count">{subscribedMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={machineSearch}
                onChange={(e) => setMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--danger"
                onClick={() => setConfirmRemoveAll(true)}
                disabled={subscribedMachinesWithDetails.length === 0}
              >
                Remove All
              </button>
            </div>
            {confirmRemoveAll && (
              <div className="ps-confirm-overlay">
                <div className="ps-confirm-dialog">
                  <p>
                    Do you really want to remove all {subscribedMachinesWithDetails.length} machines from this
                    background?
                  </p>
                  <div className="ps-confirm-actions">
                    <button
                      type="button"
                      className="ps-btn ps-btn--ghost"
                      onClick={() => setConfirmRemoveAll(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ps-btn ps-btn--danger-solid"
                      onClick={handleRemoveAllMachines}
                    >
                      Yes, remove all
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="ps-table-scroll">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('name')}>
                      Name {sortArrow(machineSortKey === 'name', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('place')}>
                      Place {sortArrow(machineSortKey === 'place', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('post')}>
                      Post {sortArrow(machineSortKey === 'post', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('province')}>
                      Province {sortArrow(machineSortKey === 'province', machineSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedMachines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ps-empty-cell">
                        {subscribedMachinesWithDetails.length === 0 ? 'None' : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedMachines.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        <td>{m.province}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--remove"
                            title="Remove from background"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMachine(m.id);
                            }}
                          >
                            -
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="ps-placeholder">Select a background to see details</div>
        )}
      </div>
      {/* Third column: available machines and machine groups */}
      <div className="ps-column ps-column--available">
        {!selectedBackground ? (
          <div className="ps-placeholder">Select a background to see details</div>
        ) : (
          <>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Available machines</h3>
              <span className="ps-column-count">{availableMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={availableMachineSearch}
                onChange={(e) => setAvailableMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--success"
                title="Add all to background"
                onClick={handleAddAllMachines}
                disabled={filteredAndSortedAvailableMachines.length === 0}
              >
                Add All
              </button>
            </div>
            <div className="ps-table-scroll ps-table-scroll--available">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('name')}>
                      Name {sortArrow(availableSortKey === 'name', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('place')}>
                      Place {sortArrow(availableSortKey === 'place', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('post')}>
                      Post {sortArrow(availableSortKey === 'post', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('province')}>
                      Province {sortArrow(availableSortKey === 'province', availableSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAvailableMachines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ps-empty-cell">
                        {availableMachinesWithDetails.length === 0
                          ? 'All machines subscribed'
                          : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedAvailableMachines.map((m) => (
                      <tr key={m.id}>
                        <td>
                          {m.name}
                          {currentBackgroundById[m.id] && (
                            <span className="ps-subs-badge">{currentBackgroundById[m.id]}</span>
                          )}
                        </td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        <td>{m.province}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Add to background"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddMachine(m.id);
                            }}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="ps-column-head ps-column-head--groups">
              <h3 className="ps-column-label">Machine groups</h3>
              <span className="ps-column-count">{filteredGroups.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by group name..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
            </div>
            <div className="ps-table-scroll ps-table-scroll--groups">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Machines</th>
                    <th>Subscribed</th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="ps-empty-cell">
                        {!machineGroups?.length ? 'No groups' : 'No matching groups'}
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group) => {
                      const total = group.machineIds?.length ?? 0;
                      const subscribed = groupSubscribedCount(group);
                      return (
                        <tr key={group.id}>
                          <td>{group.name}</td>
                          <td>{total}</td>
                          <td>{subscribed}</td>
                          <td className="ps-td-action ps-td-action--pair">
                            <button
                              className="ps-icon-btn ps-icon-btn--remove"
                              title="Remove all machines in this group from background"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveGroup(group);
                              }}
                              disabled={subscribed === 0}
                            >
                              -
                            </button>
                            <button
                              className="ps-icon-btn ps-icon-btn--add"
                              title="Add all machines in this group to background"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddGroup(group);
                              }}
                              disabled={total === 0 || subscribed === total}
                            >
                              +
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BackgroundAssign;
